// Google Analytics event tracking utility functions

export const isAnalyticsEnabled = () => {
  // Check each condition separately for better debugging
  const isEnabledInEnv = process.env.VUE_APP_GA_ENABLED === 'true';
  const isGtagDefined = typeof window.gtag === 'function';
  return isEnabledInEnv && isGtagDefined;
};

export const trackEvent = (eventName, eventParams = {}) => {
  if (isAnalyticsEnabled()) {
    window.gtag('event', eventName, {
      ...eventParams,
      non_interaction: false
    });
  }
};

// Pre-defined event tracking functions
export const trackImageUpload = (fileSize, status = 'attempt', filename = '') => {
  trackEvent('image_upload', {
    file_size: fileSize,
    status: status, // 'attempt', 'success', or 'failed'
    filename: filename
  });
};

export const trackSignIn = (success = true) => {
  trackEvent('sign_in', {
    status: success ? 'success' : 'failed'
  });
};

export const trackSignUp = (success = true) => {
  trackEvent('sign_up', {
    status: success ? 'success' : 'failed'
  });
};

// Add new tracking function for feature demo interactions
export const trackFeatureDemo = (action, featureTitle) => {
  trackEvent('feature_demo_interaction', {
    action: action, // 'view', 'next', 'previous'
    feature: featureTitle
  });
};

// Add specific analysis event tracking functions
export const trackAnalysisCategoryView = (categoryName, score) => {
  trackEvent('analysis_category_expanded', {
    category_name: categoryName,
    category_score: score
  });
};

export const trackPremiumUpsellClick = (categoryName) => {
  trackEvent('analysis_premium_click', {
    category_name: categoryName
  });
};

// Simplify room evaluation tracking to just expansion and premium clicks
export const trackRoomExpanded = (roomName, overallRating) => {
  trackEvent('room_expanded', {
    room_name: roomName,
    overall_rating: overallRating
  });
};

export const trackRoomPremiumClick = (roomName) => {
  trackEvent('room_premium_click', {
    room_name: roomName
  });
};

// Add improvement suggestion tracking
export const trackSuggestionPremiumClick = (suggestionTitle) => {
  trackEvent('suggestion_premium_click', {
    suggestion_title: suggestionTitle
  });
};

// Active CTA copy variant for cost-efficiency premium messages.
// Today there is only variant 'a' (section-specific copy from
// analysis.costEfficiency.premiumCta.<section>). When we wire a real
// A/B test, swap this stub for a PostHog feature-flag lookup keyed on
// the user/anon id so the same caller stays in the same arm.
export const getCostEfficiencyCtaVariant = () => 'a';

// Add cost efficiency analysis tracking
export const trackCostEfficiencyPremiumClick = (section, ctaVariant = getCostEfficiencyCtaVariant()) => {
  trackEvent('cost_efficiency_premium_click', {
    section_name: section,
    cta_variant: ctaVariant
  });
};

// Add PDF export tracking
export const trackPdfExport = () => {
  trackEvent('analysis_pdf_export', {
    timestamp: new Date().toISOString()
  });
};

// Add FAQ interaction tracking
export const trackFaqInteraction = (questionKey, panelIndex) => {
  trackEvent('faq_panel_expanded', {
    question: questionKey,
    panel_index: panelIndex
  });
};

// Payment tracking
export const trackPaymentSuccess = (amount, currency, transactionId) => {
  trackEvent('payment_success', {
    amount: amount,
    currency: currency,
    transaction_id: transactionId,
    timestamp: new Date().toISOString()
  });
};

export const trackPaymentFailure = () => {
  trackEvent('payment_error')
}

/**
 * Tracks a purchase event for e-commerce analytics.
 * @param {object} purchaseData - The purchase data.
 * @param {string} purchaseData.transaction_id - The transaction ID.
 * @param {number} purchaseData.value - The total value of the purchase.
 * @param {string} purchaseData.currency - The currency of the purchase.
 * @param {Array<object>} purchaseData.items - The items purchased.
 */
export function trackPurchase(purchaseData) {
  trackEvent('purchase', purchaseData)
}

// Add rate limit error tracking
export const trackRateLimitError = (fileSize, filename) => {
  trackEvent('trial_limit_error', {
    file_size: fileSize,
    filename: filename,
    timestamp: new Date().toISOString()
  });
};

// Add upload feedback tracking
export const trackUploadFeedback = (feedbackData) => {
  trackEvent('upload_feedback', {
    expectations: feedbackData.expectations,
    purpose: feedbackData.purpose,
    experience_level: feedbackData.experienceLevel,
    skipped: feedbackData.skipped,
    timestamp: new Date().toISOString()
  });
};

// Add user query analysis tracking
export const trackUserQueryPremiumClick = () => {
  trackEvent('user_query_premium_click', {
    section: 'user_requirements_analysis',
    timestamp: new Date().toISOString()
  });
};

// Add pricing click tracking
export const trackPricingClick = (source) => {
  trackEvent('pricing_click', {
    source: source, // 'nav_menu', 'mobile_menu', or 'user_menu'
    timestamp: new Date().toISOString()
  });
};

export const trackAnalysisLoaded = (payload = {}) => {
  trackEvent('analysis_loaded', payload);
};

export const trackAnalysisTabView = (payload = {}) => {
  trackEvent('analysis_tab_view', payload);
};

export const trackUnlockCtaClicked = (payload = {}) => {
  trackEvent('unlock_cta_clicked', payload);
};

export const trackAuthGateOpened = (payload = {}) => {
  trackEvent('auth_gate_opened', payload);
};

export const trackPaypalOpened = (payload = {}) => {
  trackEvent('paypal_opened', payload);
};

export const trackPersonalizedTeaserRequested = (payload = {}) => {
  trackEvent('personalized_teaser_requested', payload);
};

export const trackPersonalizedTeaserLoadingSeen = (payload = {}) => {
  trackEvent('personalized_teaser_loading_seen', payload);
};

export const trackPersonalizedTeaserReady = (payload = {}) => {
  trackEvent('personalized_teaser_ready', payload);
};

export const trackPersonalizedTeaserFailed = (payload = {}) => {
  trackEvent('personalized_teaser_failed', payload);
};

export const trackPersonalizedTeaserImpression = (payload = {}) => {
  trackEvent('personalized_teaser_impression', payload);
};
